import { v4 as uuid } from 'uuid';
import { deleteOwnedMedia, uploadOwnedMedia } from '../../util/ownedMedia';

export async function uploadPaidEquipmentImage({
    studioId,
    equipmentId,
    imageData,
    uploadMedia = uploadOwnedMedia,
}) {
    if (!studioId) {
        throw new Error('Studio konnte nicht zugeordnet werden.');
    }
    if (!equipmentId) {
        throw new Error('Equipment konnte nicht zugeordnet werden.');
    }
    if (!imageData?.file) {
        return imageData?.key || imageData?.fileName || null;
    }

    const uploadResult = await uploadMedia({
        file: imageData.file,
        entityType: 'PAID_EQUIPMENT',
        entityId: equipmentId,
        parentEntityId: studioId,
        mediaKind: 'IMAGE',
        slot: 'equipment',
        fileName: imageData.file.name || imageData.fileName || 'equipment.jpg',
    });

    return uploadResult.key;
}

function normalizePaidEquipmentPayload(item, imageKey) {
    return {
        category: item?.category || '',
        name: (item?.nameModel || '').trim(),
        price: Number(item?.pricePerPiece || 0),
        quantity: Math.max(1, Number(item?.quantity || 1)),
        description: item?.description || null,
        imageKey,
    };
}

function hasPaidEquipmentChanged(existing, normalizedPayload) {
    return (
        (existing.category || '') !== normalizedPayload.category ||
        (existing.nameModel || '') !== normalizedPayload.name ||
        Number(existing.pricePerPiece || 0) !== normalizedPayload.price ||
        Math.max(1, Number(existing.quantity || 1)) !== normalizedPayload.quantity ||
        (existing.description || '') !== (normalizedPayload.description || '') ||
        (existing.imageKey || null) !== (normalizedPayload.imageKey || null)
    );
}

async function cleanupUploadedPaidEquipmentImages({
    studioId,
    pendingUploadedMedia,
    deleteMedia,
}) {
    if (!pendingUploadedMedia.length) return null;

    const cleanupResults = await Promise.allSettled(
        pendingUploadedMedia.map(({ key, entityId }) => deleteMedia({
            key,
            entityType: 'PAID_EQUIPMENT',
            entityId,
            parentEntityId: studioId,
            mediaKind: 'IMAGE',
        }))
    );

    return cleanupResults.find((result) => result.status === 'rejected')?.reason || null;
}

function markUploadedMediaCommitted(pendingUploadedMedia, mediaEntry) {
    if (!mediaEntry) return;

    const index = pendingUploadedMedia.indexOf(mediaEntry);
    if (index >= 0) {
        pendingUploadedMedia.splice(index, 1);
    }
}

export async function syncStudioEditorPaidEquipmentChanges({
    studio,
    stagedItems,
    createPaidEquipmentItem,
    updatePaidEquipmentItem,
    deletePaidEquipmentItem,
    refetchQueries,
    uploadMedia = uploadOwnedMedia,
    deleteMedia = deleteOwnedMedia,
    idGenerator = uuid,
}) {
    if (!studio?.id) {
        throw new Error('Studio konnte nicht zugeordnet werden.');
    }

    const existingItems = Array.isArray(studio?.optionalBookableEquipment)
        ? studio.optionalBookableEquipment
        : [];
    const existingById = new Map(existingItems.filter((it) => it?.id).map((it) => [it.id, it]));
    const stagedExistingIds = new Set(
        (stagedItems || [])
            .filter((it) => it?.id && existingById.has(it.id))
            .map((it) => it.id)
    );

    const pendingUploadedMedia = [];
    let createCount = 0;
    let updateCount = 0;
    let deleteCount = 0;

    try {
        for (let i = 0; i < (stagedItems || []).length; i += 1) {
            const item = stagedItems[i];
            const existing = item?.id ? existingById.get(item.id) : null;
            const itemId = existing?.id || idGenerator();
            let imageKey = item?.imageKey || null;
            let uploadedMediaEntry = null;

            if (item?.image?.file) {
                imageKey = await uploadPaidEquipmentImage({
                    studioId: studio.id,
                    equipmentId: itemId,
                    imageData: item.image,
                    uploadMedia,
                });
                uploadedMediaEntry = { key: imageKey, entityId: itemId };
                pendingUploadedMedia.push(uploadedMediaEntry);
            }

            const normalizedPayload = normalizePaidEquipmentPayload(item, imageKey);

            if (existing) {
                if (hasPaidEquipmentChanged(existing, normalizedPayload)) {
                    await updatePaidEquipmentItem({
                        variables: {
                            input: {
                                id: existing.id,
                                ...normalizedPayload,
                            },
                        },
                    });
                    updateCount += 1;
                }
                markUploadedMediaCommitted(pendingUploadedMedia, uploadedMediaEntry);
                continue;
            }

            await createPaidEquipmentItem({
                variables: {
                    input: {
                        id: itemId,
                        studioID: studio.id,
                        ...normalizedPayload,
                        sortOrder: typeof item?.sortOrder === 'number' ? item.sortOrder : i,
                    },
                },
            });
            createCount += 1;
            markUploadedMediaCommitted(pendingUploadedMedia, uploadedMediaEntry);
        }

        for (const existing of existingItems) {
            if (!existing?.id) continue;
            if (stagedExistingIds.has(existing.id)) continue;
            await deletePaidEquipmentItem({ variables: { input: { id: existing.id } } });
            deleteCount += 1;
        }

        await refetchQueries?.({
            include: ['GetStudio', 'GetStudioPublic'],
        });

        return {
            createCount,
            updateCount,
            deleteCount,
        };
    } catch (error) {
        const cleanupError = await cleanupUploadedPaidEquipmentImages({
            studioId: studio.id,
            pendingUploadedMedia,
            deleteMedia,
        });
        if (cleanupError) {
            error.cleanupError = cleanupError;
        }
        throw error;
    }
}
